import React, { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface WaterProps {
  position: [number, number, number];
  scale?: number;
  type?: 'pond' | 'lake' | 'river';
}

export const Water: React.FC<WaterProps> = ({
  position,
  scale = 1,
  type = 'pond',
}) => {
  const waterColor = useMemo(() => {
    // Different water colors based on type
    switch (type) {
      case 'lake':
        return new THREE.Color('#1976D2').multiplyScalar(
          0.8 + Math.random() * 0.3
        );
      case 'river':
        return new THREE.Color('#42A5F5').multiplyScalar(
          0.8 + Math.random() * 0.3
        );
      default: // pond
        return new THREE.Color('#29B6F6').multiplyScalar(
          0.8 + Math.random() * 0.3
        );
    }
  }, [type]);

  // Generate appropriate geometry parameters based on type
  const geometryArgs = useMemo(() => {
    switch (type) {
      case 'lake':
        // Larger, more complex shape for lakes
        return [8, 12];
      case 'river':
        // Elongated shape for rivers
        return [3, 12, 1, 4];
      default: // pond
        // Small round pond
        return [3, 8];
    }
  }, [type]);

  // Small random rotation for natural look
  const rotation = useMemo(() => {
    return [Math.PI / 2, 0, Math.random() * Math.PI * 2];
  }, []);

  // Use refs instead of state for animation
  const timeRef = useRef(0);
  const materialRef = useRef<THREE.MeshStandardMaterial>(null);
  const vectorRef = useRef(new THREE.Vector2(0, 0));

  // Use useFrame for animation instead of useState/useEffect
  useFrame((_, delta) => {
    timeRef.current += delta * 0.5;

    // Update vector directly without causing re-renders
    if (vectorRef.current) {
      vectorRef.current.set(
        Math.sin(timeRef.current) * 0.05,
        Math.cos(timeRef.current) * 0.05
      );
    }
  });

  return (
    <mesh
      position={[position[0], position[1] + 0.05, position[2]]}
      rotation={rotation as any}
      scale={[scale, scale, scale]}
      receiveShadow
    >
      {type === 'pond' || type === 'lake' ? (
        <circleGeometry args={geometryArgs as [number, number]} />
      ) : (
        <planeGeometry
          args={geometryArgs as [number, number, number, number]}
        />
      )}
      <meshStandardMaterial
        ref={materialRef}
        color={waterColor.getHex()}
        transparent
        opacity={0.8}
        metalness={0.2}
        roughness={0.1}
        side={THREE.DoubleSide}
        displacementScale={0.05}
      >
        <primitive object={vectorRef.current} attach="displacementBias" />
      </meshStandardMaterial>
    </mesh>
  );
};
